import { API_BASE_URL as BASE } from '@/lib/apiBase'
import { authFetch, authHeaders, handleUnauthorized } from '@/lib/authFetch'
import type {
  ApiStorageNode,
  ApiUnresolvedLocation,
  AuditPage,
  StorageAuditEntry,
  ZoneAuditEntry,
  DeletableSummary,
  ReresolveSummary,
  CapacitySummaryRow,
  StorageCondition,
  StorageConditionsReference,
  StorageConditionInput,
  StorageConditionMeta,
  StorageConditionPatch,
  UnplacedLot,
  ContentsResult,
  FreeSlots,
  FreeSpaceResult,
  LocateResult,
  SlotLabelScheme,
  CompactionResult,
  CompactionSummary,
  GridCells,
  LayoutTemplate,
  StorageTree,
  StorageTreeNode,
  StorageCapacity,
  StorageNodeKind,
} from '@/types/storage'

// ─── Request payload shapes (mirror cgt-backend storage DTOs) ───────────────

/** Body for `POST /inventory/storage-nodes`. */
export type CreateStorageNodePayload = {
  /** Omit to create a root — which then requires `equipmentId`. */
  parentId?: string
  /** The Purpose=Storage unit a root represents. Rejected on a child. */
  equipmentId?: string
  kind: StorageNodeKind
  code: string
  name?: string
  /** Supplying both mints the grid's cells immediately. */
  gridRows?: number
  gridColumns?: number
  /** What the grid mints. Defaults to POSITION. */
  childKind?: StorageNodeKind
  scheme?: SlotLabelScheme
  /** SEQUENTIAL prefix, defaulted by child kind (RACK→R, BOX→C, POSITION→P). */
  prefix?: string
}

export type UpdateStorageNodePayload = {
  code?: string
  /** Null clears a custom name, so the node displays by its code alone. */
  name?: string | null
  active?: boolean
  /**
   * Null clears it, so the node inherits from the unit above instead.
   *
   * Setting this directly detaches the node from any storage profile — the
   * value no longer came from one. The app sends `storageProfileId` instead;
   * this remains for the seed and import paths.
   */
  providesCondition?: StorageCondition | null
  /**
   * The inventory Storage Profile this location is operated to. Assigning one
   * derives `providesCondition` from it, and editing that profile re-derives it
   * here. Null clears both.
   */
  storageProfileId?: string | null
  /** The picking zone this location belongs to. Null clears it. */
  zoneId?: string | null
}

/** Body for `POST /inventory/storage-nodes/:id/grid`. */
export type DeclareGridPayload = {
  rows: number
  columns: number
  /** What the grid mints. Defaults to POSITION. */
  childKind?: StorageNodeKind
  scheme?: SlotLabelScheme
  /** SEQUENTIAL prefix, defaulted by child kind (RACK→R, BOX→C, POSITION→P). */
  prefix?: string
}

/**
 * One level of `POST /inventory/storage-nodes/:id/layout`, applied to every
 * node the level above created. Either counted (`rows` × `columns`) or named
 * (`codes`) — never both.
 */
export type LayoutLevelPayload = {
  kind: StorageNodeKind
  codes?: string[]
  rows?: number
  columns?: number
  scheme?: SlotLabelScheme
  prefix?: string
}

export type BuildLayoutResult = {
  rootId: string
  created: number
  /** Nodes created at each level, outermost first. */
  byLevel: number[]
}

/** Paging for an audit trail. The server caps `take` at 200 and 400s above it. */
export type AuditPageParams = {
  skip?: number
  take?: number
  /**
   * Include everything that happened anywhere BENEATH the node, not just at it.
   * A unit or rack has no events of its own — put-aways and blocks land on the
   * positions below — so without this a freezer's trail reads as empty.
   */
  subtree?: boolean
}

export type ListStorageNodesParams = {
  parentId?: string
  /** Prefix match on the materialised path — "everything under this freezer". */
  pathPrefix?: string
  kind?: StorageNodeKind
  rootsOnly?: boolean
}

export type FreeSlotsParams = {
  /** Most slots to return; the server defaults to 200 and clamps at 1,000. */
  limit?: number
  /** Only slots inheriting exactly this condition, as put-away checks it. */
  condition?: StorageCondition
  /** A stored slot to return even though it is taken: where the item is now. */
  include?: string
}

async function request<T>(path: string, tenantId: string, init?: RequestInit): Promise<T> {
  const res = await authFetch(`${BASE}${path}`, {
    ...init,
    headers: authHeaders({
      'Content-Type': 'application/json',
      'X-Tenant-Id': tenantId,
      ...init?.headers,
    }),
  })
  handleUnauthorized(res.status)
  if (!res.ok) {
    const body = (await res.json().catch(() => null)) as { message?: string | string[] } | null
    const message = Array.isArray(body?.message) ? body.message.join('; ') : body?.message
    // The status rides along so a screen can tell "not allowed" from "broken".
    throw Object.assign(new Error(message || `Request failed: ${res.status}`), { status: res.status })
  }
  return res.json() as Promise<T>
}

function query(params: Record<string, string | number | boolean | undefined>): string {
  const search = new URLSearchParams()
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined) search.set(key, String(value))
  }
  const qs = search.toString()
  return qs ? `?${qs}` : ''
}

export const storageApi = {
  listNodes: (tenantId: string, params: ListStorageNodesParams = {}) =>
    request<ApiStorageNode[]>(`/inventory/storage-nodes${query({ ...params })}`, tenantId),

  getNode: (tenantId: string, id: string) =>
    request<ApiStorageNode>(`/inventory/storage-nodes/${id}`, tenantId),

  /** A node and everything beneath it, in path order. */
  getSubtree: (tenantId: string, id: string) =>
    request<ApiStorageNode[]>(`/inventory/storage-nodes/${id}/subtree`, tenantId),

  /**
   * A unit's free slots, for a slot picker (z8nrz7fqfy).
   *
   * What every picker reads instead of `getSubtree`, which lists stored rows
   * only: a slot with no row of its own (declared by its box's grid) was
   * missing, so an empty rack read as full — and the whole unit was shipped to
   * offer a few slots. This returns the first page of free slots, declared ones
   * included, with the containers above them; `freeTotal` is the real count.
   */
  getFreeSlots: (tenantId: string, id: string, params: FreeSlotsParams = {}) =>
    request<FreeSlots>(`/inventory/storage-nodes/${id}/free-slots${query({ ...params })}`, tenantId),

  /**
   * The containers of a unit, each with its own capacity. No slots.
   *
   * What the hierarchy pane draws. It lists racks and boxes and never a slot,
   * so sending it every position was sending tens of thousands of rows to
   * render a few dozen — and the fill bars it used to add up in the browser are
   * computed on the server now, which is what makes leaving them out possible.
   * Measured on a 49,641-node freezer: 1,569 ms and 29.4 MB became 170 ms and
   * 0.84 MB, whether or not the unit has been compacted.
   */
  getTree: (tenantId: string, id: string, depth?: number) =>
    request<StorageTree>(`/inventory/storage-nodes/${id}/tree${query({ depth })}`, tenantId),

  /**
   * Find a container anywhere under a unit.
   *
   * The pane holds only the levels it has opened, so it cannot answer "where is
   * C05" from memory — and searching is exactly when someone does not know
   * which branch to open. Containers only, capped by the server.
   */
  searchContainers: (tenantId: string, id: string, q: string) =>
    request<StorageTreeNode[]>(
      `/inventory/storage-nodes/${id}/search${query({ q })}`,
      tenantId,
    ),

  /**
   * One container's cells, for a slot map.
   *
   * Scoped to a single container on purpose. The map draws one box at a time,
   * so reading a whole unit to render one shelf was always the wrong shape —
   * it is what made a large freezer a 29 MB response. Cells that have no row of
   * their own come back derived from the box's grid declaration, in the same
   * shape as stored ones.
   */
  getGridCells: (tenantId: string, id: string) =>
    request<GridCells>(`/inventory/storage-nodes/${id}/cells`, tenantId),

  /** Derived capacity — computed server-side on every read, never stored. */
  getCapacity: (tenantId: string, id: string) =>
    request<StorageCapacity>(`/inventory/storage-nodes/${id}/capacity`, tenantId),

  /** Free-text location codes the migration parked rather than guessed at. */
  listUnresolved: (tenantId: string) =>
    request<ApiUnresolvedLocation[]>('/inventory/storage-nodes/unresolved', tenantId),

  /**
   * Try every parked code again against the shelves as they are now.
   *
   * The migration ran before any layout was declared, so it had nothing to
   * match against. This is the second attempt.
   */
  reresolveUnresolved: (tenantId: string) =>
    request<ReresolveSummary>('/inventory/storage-nodes/unresolved/re-resolve', tenantId, {
      method: 'POST',
    }),

  /**
   * What compacting this unit would remove. Read-only.
   *
   * A unit built before positions were declared carries a row per slot. This
   * reports how many of those rows the declarations above them already describe
   * exactly, and how many are kept because they record something a declaration
   * cannot — occupied, blocked, renamed, or carrying their own condition.
   */
  describeCompaction: (tenantId: string, id: string) =>
    request<CompactionSummary>(`/inventory/storage-nodes/${id}/compactable`, tenantId),

  /**
   * Fold a unit's redundant cell rows back into the declarations describing them.
   *
   * Nothing observable changes: every slot keeps its address, its capacity and
   * its place in the slot map, because a row is only removed when the
   * declaration reproduces it exactly. What changes is the size of the unit —
   * measured on a 48,000-slot freezer, reading it went from 1,816 ms and
   * 29.4 MB to 48 ms and 1.0 MB.
   */
  compact: (tenantId: string, id: string, reason?: string) =>
    request<CompactionResult>(
      `/inventory/storage-nodes/${id}/compact${query({ reason })}`,
      tenantId,
      { method: 'POST' },
    ),

  /** What deleting this node and its layout would remove. Read-only. */
  describeDeletable: (tenantId: string, id: string) =>
    request<DeletableSummary>(`/inventory/storage-nodes/${id}/deletable`, tenantId),

  /**
   * Delete a node and its whole layout. Refused by the server when any lot or
   * pick-list line references anything beneath it.
   */
  deleteSubtree: (tenantId: string, id: string) =>
    request<{ id: string; deleted: number }>(
      `/inventory/storage-nodes/${id}/subtree`,
      tenantId,
      { method: 'DELETE' },
    ),

  /** The Figure-9 table: one row per storage unit, every number derived. */
  capacitySummary: (tenantId: string) =>
    request<CapacitySummaryRow[]>('/inventory/storage-nodes/capacity-summary', tenantId),

  /** "Where is lot X" — finds LOCATED stock by lot number or material. */
  locate: (tenantId: string, q: string) =>
    request<LocateResult[]>(`/inventory/storage-nodes/locate${query({ q })}`, tenantId),

  /** Lots held anywhere beneath a node, each with its position path. */
  contents: (tenantId: string, id: string) =>
    request<ContentsResult>(`/inventory/storage-nodes/${id}/contents`, tenantId),

  /** Stock already in the hierarchy, for a "jump to a lot" picker. */
  placed: (tenantId: string) =>
    request<LocateResult[]>('/inventory/storage-nodes/placed', tenantId),

  /** The put-away queue: stock with no slot yet. */
  unplaced: (tenantId: string, requires?: StorageCondition | null) =>
    request<UnplacedLot[]>(
      `/inventory/storage-nodes/unplaced${requires ? query({ requires }) : ''}`,
      tenantId,
    ),

  /** Reference data for the §5.2 pickers — served, never duplicated locally. */
  conditions: (tenantId: string) =>
    request<StorageConditionsReference>('/inventory/storage-nodes/conditions', tenantId),

  /** Add one of the tenant's own storage conditions; the server generates its code. */
  createCondition: (tenantId: string, input: StorageConditionInput) =>
    request<StorageConditionMeta>('/inventory/storage-conditions', tenantId, {
      method: 'POST',
      body: JSON.stringify(input),
    }),

  /** Rename, re-describe, reorder or switch a condition on/off. The code never changes. */
  updateCondition: (tenantId: string, id: string, patch: StorageConditionPatch) =>
    request<StorageConditionMeta>(`/inventory/storage-conditions/${id}`, tenantId, {
      method: 'PATCH',
      body: JSON.stringify(patch),
    }),

  /** Delete a condition nothing uses; 409 says where it is used otherwise. */
  deleteCondition: (tenantId: string, id: string) =>
    request<{ ok: true }>(`/inventory/storage-conditions/${id}`, tenantId, { method: 'DELETE' }),

  /** Units with room; with `requires`, only those at a compatible condition. */
  freeSpace: (tenantId: string, requires?: StorageCondition | null) =>
    request<FreeSpaceResult>(
      `/inventory/storage-nodes/free-space${requires ? `?requires=${encodeURIComponent(requires)}` : ''}`,
      tenantId,
    ),

  /** Switch a declared grid's label scheme — every cell renamed, audited. */
  relabel: (tenantId: string, id: string, payload: { scheme: SlotLabelScheme; prefix?: string }) =>
    request<{ relabeled: number }>(`/inventory/storage-nodes/${id}/relabel`, tenantId, {
      method: 'POST',
      body: JSON.stringify(payload),
    }),

  createNode: (tenantId: string, payload: CreateStorageNodePayload) =>
    request<ApiStorageNode>('/inventory/storage-nodes', tenantId, {
      method: 'POST',
      body: JSON.stringify(payload),
    }),

  declareGrid: (tenantId: string, id: string, payload: DeclareGridPayload) =>
    request<ApiStorageNode & { mintedPositions: number }>(
      `/inventory/storage-nodes/${id}/grid`,
      tenantId,
      { method: 'POST', body: JSON.stringify(payload) },
    ),

  /**
   * A whole layout in one request.
   *
   * Preferred over looping `declareGrid`: that costs one request per container,
   * which trips the API's rate limiter on a large plan and can only fail
   * half-applied. This is one transaction — it either exists or it does not.
   */
  buildLayout: (
    tenantId: string,
    id: string,
    levels: LayoutLevelPayload[],
    /**
     * What a COPY is carrying across. Sent on the build rather than as
     * follow-up requests: blocked slots one `setBlocked` call each would be N
     * requests against the rate limiter this endpoint exists to avoid, and a
     * condition applied afterwards leaves the unit briefly open at no stated
     * temperature.
     */
    copy?: {
      providesCondition?: StorageCondition
      storageProfileId?: string
      blockedCells?: string[]
    },
  ) =>
    request<BuildLayoutResult>(`/inventory/storage-nodes/${id}/layout`, tenantId, {
      method: 'POST',
      body: JSON.stringify({ levels, ...copy }),
    }),

  /**
   * An existing unit described as a layout, for copying onto another.
   *
   * The whole of "copy this freezer" is this read plus `buildLayout` — a
   * layout is already expressible as levels, so there is no second build path.
   */
  layoutTemplate: (tenantId: string, id: string) =>
    request<LayoutTemplate>(`/inventory/storage-nodes/${id}/layout-template`, tenantId),

  updateNode: (tenantId: string, id: string, payload: UpdateStorageNodePayload) =>
    request<ApiStorageNode>(`/inventory/storage-nodes/${id}`, tenantId, {
      method: 'PATCH',
      body: JSON.stringify(payload),
    }),

  moveNode: (tenantId: string, id: string, newParentId: string | null) =>
    request<ApiStorageNode>(`/inventory/storage-nodes/${id}/parent`, tenantId, {
      method: 'PATCH',
      body: JSON.stringify({ newParentId }),
    }),

  /** Blocking an occupied position is refused by the server (409). */
  setBlocked: (tenantId: string, id: string, blocked: boolean) =>
    request<ApiStorageNode>(`/inventory/storage-nodes/${id}/blocked`, tenantId, {
      method: 'PATCH',
      body: JSON.stringify({ blocked }),
    }),

  /** Put a lot away into a POSITION, or clear its location with `null`. */
  setLotLocation: (tenantId: string, lotId: string, storageNodeId: string | null) =>
    request<{ id: string; storageNodeId: string | null }>(
      `/inventory/storage-nodes/lots/${lotId}/location`,
      tenantId,
      { method: 'PATCH', body: JSON.stringify({ storageNodeId }) },
    ),

  /**
   * Put a therapy product into a POSITION, or clear it with `null`. The server
   * refuses a slot already holding anything — one product per slot.
   */
  setProductLocation: (tenantId: string, productId: string, storageNodeId: string | null) =>
    request<{ id: string; storageNodeId: string | null }>(
      `/inventory/storage-nodes/products/${productId}/location`,
      tenantId,
      { method: 'PATCH', body: JSON.stringify({ storageNodeId }) },
    ),

  deleteNode: (tenantId: string, id: string) =>
    request<{ id: string }>(`/inventory/storage-nodes/${id}`, tenantId, { method: 'DELETE' }),

  /**
   * One node's audit trail, newest first.
   *
   * Readable after the node itself is deleted — the server proves tenancy from
   * the rows when the subject is gone, which is the whole point of an
   * append-only trail with no foreign key.
   */
  nodeAudit: (tenantId: string, id: string, params: AuditPageParams = {}) =>
    request<AuditPage<StorageAuditEntry>>(
      `/inventory/storage-nodes/${id}/audit${query({ ...params })}`,
      tenantId,
    ),

  /**
   * One lot's trail across every position it has ever occupied.
   *
   * Only stock moved through the app has one: lots written directly by the seed
   * or an import never passed through the put-away path, so an empty result is
   * "no recorded history", not "never stored anywhere".
   */
  lotAudit: (tenantId: string, lotId: string, params: AuditPageParams = {}) =>
    request<AuditPage<StorageAuditEntry>>(
      `/inventory/storage-nodes/audit${query({ lotId, ...params })}`,
      tenantId,
    ),

  /** One zone's audit trail, newest first. */
  zoneAudit: (tenantId: string, id: string, params: AuditPageParams = {}) =>
    request<AuditPage<ZoneAuditEntry>>(
      `/inventory/zones/${id}/audit${query({ ...params })}`,
      tenantId,
    ),
}
